import React, { useState, useEffect } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    BookOpen,
    UserCheck,
    FileSignature,
    Zap,
    ClipboardCheck,
    Sparkles,
    Calculator,
    ReceiptText,
    ScrollText,
    ShieldCheck,
    HelpCircle,
    MessageCircle,
    Play,
    Clock,
    RefreshCw,
    XCircle,
    Percent,
    Wallet,
    Smartphone
} from 'lucide-react';

// Seções marcadas com "PLACEHOLDER" aguardam material real do usuário
// (prints de fatura, documento de referência do cálculo, contrato de adesão).
const ManualConsumidorView: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [activeFaq, setActiveFaq] = useState<number | null>(0);

    const slidesCount = 12;

    const goToSlide = (index: number) => {
        if (index >= 0 && index < slidesCount) {
            setCurrentSlide(index);
        }
    };

    const nextSlide = () => {
        if (currentSlide < slidesCount - 1) {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const prevSlide = () => {
        if (currentSlide > 0) {
            setCurrentSlide(currentSlide - 1);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight' || e.key === ' ') {
                nextSlide();
            } else if (e.key === 'ArrowLeft') {
                prevSlide();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [currentSlide]);

    useEffect(() => {
        const originalTitle = document.title;
        const metaDescription = document.querySelector('meta[name="description"]');
        const originalDescription = metaDescription ? metaDescription.getAttribute('content') : '';

        document.title = "Manual do Consumidor | Vigor Energy";
        if (metaDescription) {
            metaDescription.setAttribute('content', "Entenda o modelo de energia por assinatura da Vigor Energy: como funciona o desconto, sua jornada como cliente e os pontos-chave do contrato.");
        } else {
            const meta = document.createElement('meta');
            meta.name = "description";
            meta.content = "Entenda o modelo de energia por assinatura da Vigor Energy.";
            document.head.appendChild(meta);
        }

        return () => {
            document.title = originalTitle;
            const currentMetaDesc = document.querySelector('meta[name="description"]');
            if (currentMetaDesc && originalDescription !== null) {
                currentMetaDesc.setAttribute('content', originalDescription);
            } else if (currentMetaDesc && originalDescription === null) {
                currentMetaDesc.remove();
            }
        };
    }, []);

    const jornadaSteps = [
        { icon: UserCheck, title: "Cadastro e simulação", desc: "Você informa seus dados e sua conta de luz para simularmos o desconto estimado." },
        { icon: ClipboardCheck, title: "Análise e aprovação", desc: "Validamos sua unidade consumidora e disponibilidade de energia nas nossas usinas." },
        { icon: FileSignature, title: "Assinatura digital", desc: "Contrato assinado 100% online, sem burocracia e sem custo de adesão." },
        { icon: Zap, title: "Ativação na distribuidora", desc: "Sua unidade é vinculada à geração da Vigor junto à distribuidora local." },
        { icon: Sparkles, title: "Primeiro desconto", desc: "O desconto passa a aparecer na sua fatura em até 90 dias após a ativação." },
    ];

    const faqItems = [
        {
            q: "A energia muda fisicamente na minha casa?",
            a: "Não. Você continua recebendo energia da mesma distribuidora, pela mesma rede elétrica. O que muda é a origem da energia (créditos gerados pelas usinas da Vigor) e o valor cobrado na fatura."
        },
        {
            q: "Existe fidelidade ou multa por cancelamento?",
            a: "Quando há período de Fidelidade previsto no seu Plano de Benefícios, cancelar antes do fim desse prazo sem justa causa pode gerar multa de até 30% do valor médio mensal multiplicado pelos meses restantes. Fora da Fidelidade, ou durante a carência, a saída não gera multa, respeitado o aviso prévio de 90 dias."
        },
        {
            q: "O desconto é fixo todo mês?",
            a: "Não. O Benefício Econômico é estimado e pode variar conforme o consumo, a disponibilidade de créditos das usinas, o reajuste tarifário da Equatorial e bandeiras tarifárias — não é uma garantia contratual de valor fixo."
        },
        {
            q: "Recebo duas faturas (distribuidora + Vigor)?",
            a: "Sim. Você recebe a fatura normal da distribuidora (já com o desconto refletido na energia compensada) e a fatura da Vigor Energy referente à energia injetada."
        }
    ];

    return (
        <div className="fixed inset-0 overflow-hidden flex flex-col items-center justify-center bg-[#5A6B3F] z-[9999]">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@400;500;600;700;800&family=Poppins:wght@300;400;500;600;700&display=swap');

        .font-baloo {
          font-family: 'Baloo 2', sans-serif !important;
        }

        .slide-transition {
          transition: opacity 0.6s ease-in-out, transform 0.6s ease-in-out;
          opacity: 0;
          pointer-events: none;
          transform: scale(0.98);
          z-index: 0;
          position: absolute;
          inset: 0;
        }

        .slide-transition.active {
          opacity: 1;
          pointer-events: auto;
          transform: scale(1);
          z-index: 10;
        }

        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .placeholder-box {
          border: 2px dashed #d1a35c;
          background: repeating-linear-gradient(
            45deg,
            rgba(239,155,30,0.06),
            rgba(239,155,30,0.06) 10px,
            rgba(239,155,30,0.12) 10px,
            rgba(239,155,30,0.12) 20px
          );
        }
      `}</style>

            <div className="relative w-full h-[100dvh] md:h-auto md:w-[95vw] lg:max-w-[1320px] md:aspect-video bg-white shadow-2xl md:rounded-2xl overflow-hidden flex-shrink-0">

                {/* SLIDE 1 (CAPA) */}
                <div className={`slide-transition ${currentSlide === 0 ? 'active' : ''} flex flex-col items-center justify-center text-gray-800 bg-gradient-to-br from-[#ffffff] via-[#fcfaf8] to-[#fceee0] p-8`}>
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/50 via-transparent to-transparent"></div>
                    <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="mb-10 transform transition hover:scale-105 duration-300">
                            <img src="/LOGO-01.png" alt="Vigor Energy Logo" className="h-28 md:h-40 object-contain drop-shadow-sm" />
                        </div>
                        <div className="flex items-center gap-2 md:gap-3 bg-white/70 border border-green-200 rounded-full px-4 md:px-5 py-1.5 md:py-2 mb-5 md:mb-6 shadow-sm">
                            <BookOpen className="w-4 h-4 md:w-5 md:h-5 text-green-700" />
                            <span className="text-xs md:text-sm font-bold tracking-widest uppercase text-green-700">Manual do Consumidor</span>
                        </div>
                        <h1 className="font-baloo text-3xl md:text-4xl lg:text-5xl font-extrabold drop-shadow-sm tracking-tight leading-tight max-w-4xl">
                            Entenda como funciona a<br className="hidden md:block" /> sua <span className="text-green-600">energia por assinatura</span>
                        </h1>
                        <p className="mt-5 md:mt-6 text-sm md:text-lg text-gray-600 max-w-2xl font-medium">
                            Um guia completo sobre o modelo, a jornada como cliente e o que muda na sua fatura.
                        </p>
                    </div>
                </div>

                {/* SLIDE 2 (O MODELO DE ASSINATURA) */}
                <div className={`slide-transition ${currentSlide === 1 ? 'active' : ''} flex flex-col items-center justify-center bg-gray-50 text-gray-800 p-8 md:p-16`}>
                    <div className="max-w-5xl w-full flex flex-col h-full justify-center">
                        <div className="flex items-center gap-4 mb-4 md:mb-8 self-center md:self-start w-full justify-center md:justify-start">
                            <div className="w-2 h-8 md:h-10 bg-green-600 rounded-full"></div>
                            <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23]">O que é energia por assinatura?</h2>
                        </div>
                        <div className="space-y-4 md:space-y-6 text-base md:text-xl leading-relaxed text-gray-600 font-light text-center md:text-left">
                            <p>
                                A Vigor Energy produz energia <strong className="font-semibold text-gray-800">100% renovável</strong> em usinas solares próprias e injeta essa energia diretamente na rede da sua distribuidora local.
                            </p>
                            <p>
                                Ao assinar um plano, você passa a receber <strong className="font-semibold text-gray-800">créditos de energia</strong> equivalentes ao seu consumo, sem precisar instalar equipamentos ou investir em placas solares.
                            </p>
                            <p>
                                Esses créditos geram um <strong className="font-bold text-green-600">desconto de até 28%</strong> aplicado diretamente na conta de luz, todos os meses, enquanto sua assinatura estiver ativa.
                            </p>
                        </div>
                    </div>
                </div>

                {/* SLIDE 3 (JORNADA DO CLIENTE) */}
                <div className={`slide-transition ${currentSlide === 2 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-6 pb-16 md:p-12`}>
                    <div className="w-full max-w-5xl flex flex-col h-full justify-center">
                        <div className="flex items-center gap-4 mb-4 md:mb-10 self-center md:self-start w-full justify-center md:justify-start">
                            <div className="w-2 h-8 md:h-10 bg-yellow-500 rounded-full"></div>
                            <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23]">Sua jornada como cliente</h2>
                        </div>
                        <div className="overflow-y-auto no-scrollbar py-2 flex flex-col justify-center flex-grow">
                            <div className="relative">
                                <div className="hidden md:block absolute top-12 left-10 right-10 h-[2px] bg-gray-300 z-0"></div>
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-5 relative z-10">
                                    {jornadaSteps.map((step, i) => (
                                        <div key={i} className="bg-white rounded-2xl shadow-lg p-5 md:p-6 flex flex-col items-center text-center border-b-4 border-green-700 transition-transform hover:-translate-y-1 duration-300">
                                            <div className="w-10 h-10 md:w-11 md:h-11 rounded-full bg-green-700 text-white flex items-center justify-center font-bold font-baloo mb-3 text-base md:text-lg">
                                                {i + 1}
                                            </div>
                                            <div className="mb-3 text-yellow-500 bg-yellow-50 p-3 rounded-full">
                                                <step.icon className="w-7 h-7 md:w-8 md:h-8" />
                                            </div>
                                            <h3 className="font-baloo text-base md:text-lg font-bold text-[#4a5d23] mb-1.5">{step.title}</h3>
                                            <p className="text-gray-600 text-sm md:text-[15px] leading-relaxed">{step.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SLIDE 4 (ENTENDENDO NA PRÁTICA - INTRO) */}
                <div className={`slide-transition ${currentSlide === 3 ? 'active' : ''} flex flex-col items-center justify-center bg-gray-50 text-gray-800 p-8 md:p-16`}>
                    <div className="max-w-4xl w-full flex flex-col items-center text-center">
                        <div className="mb-5 md:mb-6 text-green-700 bg-green-50 p-4 md:p-5 rounded-full">
                            <ReceiptText className="w-10 h-10 md:w-12 md:h-12" />
                        </div>
                        <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23] mb-4 md:mb-6">Entendendo na prática</h2>
                        <p className="text-base md:text-xl text-gray-600 leading-relaxed max-w-2xl">
                            A seguir, comparamos a sua fatura <strong className="text-gray-800 font-semibold">antes</strong> de assinar a Vigor Energy com a sua fatura <strong className="text-green-600 font-semibold">depois</strong> da adesão — linha por linha.
                        </p>
                    </div>
                </div>

                {/* SLIDE 5 (ANTES x DEPOIS) */}
                <div className={`slide-transition ${currentSlide === 4 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-6 pb-16 md:p-10`}>
                    <div className="w-full max-w-6xl flex flex-col h-full justify-center z-10">
                        <h2 className="font-baloo text-3xl md:text-4xl font-extrabold text-[#4a5d23] mb-6 md:mb-8 text-center">Fatura: Antes vs. Depois</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 flex-grow overflow-y-auto no-scrollbar pb-2">
                            {/* ANTES: uma única fatura, valor cheio */}
                            <div className="flex flex-col">
                                <div className="text-center font-bold text-gray-700 uppercase text-xs md:text-sm tracking-widest mb-2 md:mb-3">Tarifa cheia (sem Vigor)</div>
                                <a href="/antes-equatorial.png" target="_blank" rel="noopener noreferrer" className="flex-grow min-h-[260px] md:min-h-[360px] rounded-2xl overflow-hidden shadow-lg border border-gray-200 bg-white flex items-center justify-center group">
                                    <img src="/antes-equatorial.png" alt="Fatura Equatorial - valor cheio, sem Vigor" className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-300" />
                                </a>
                            </div>
                            {/* DEPOIS: duas cobranças somadas */}
                            <div className="flex flex-col">
                                <div className="text-center font-bold text-green-700 uppercase text-xs md:text-sm tracking-widest mb-2 md:mb-3">Com desconto Vigor Energy</div>
                                <div className="flex-grow flex flex-col gap-1 md:gap-2">
                                    <a href="/depois-equatorial.png" target="_blank" rel="noopener noreferrer" className="flex-1 min-h-[110px] md:min-h-[160px] rounded-2xl overflow-hidden shadow-md border border-gray-200 bg-white flex items-center justify-center group">
                                        <img src="/depois-equatorial.png" alt="Fatura Equatorial - custo de disponibilidade" className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-300" />
                                    </a>
                                    <div className="flex justify-center text-gray-400 font-bold text-lg md:text-xl leading-none">+</div>
                                    <a href="/depois-vigorenergy.png" target="_blank" rel="noopener noreferrer" className="flex-1 min-h-[110px] md:min-h-[160px] rounded-2xl overflow-hidden shadow-md border border-green-200 bg-white flex items-center justify-center group">
                                        <img src="/depois-vigorenergy.png" alt="Boleto Vigor Energy com desconto" className="w-full h-full object-contain group-hover:scale-[1.02] transition-transform duration-300" />
                                    </a>
                                </div>
                            </div>
                        </div>
                        <p className="text-center text-[12px] md:text-sm text-gray-600 mt-4 md:mt-5 max-w-3xl mx-auto">
                            Ao assinar a Vigor Energy, você passa a receber <strong className="text-gray-800">duas cobranças</strong>: a fatura da Equatorial (apenas o custo de disponibilidade) e o boleto da Vigor Energy já com o desconto aplicado. Somadas, as duas cobranças ficam mais baratas do que a tarifa cheia.
                        </p>
                    </div>
                </div>

                {/* SLIDE 6 (ORIGEM DOS COMPONENTES DE CÁLCULO) */}
                <div className={`slide-transition ${currentSlide === 5 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-6 pb-16 md:p-12`}>
                    <div className="w-full max-w-5xl flex flex-col h-full justify-center">
                        <div className="flex items-center gap-4 mb-3 md:mb-6 self-center md:self-start w-full justify-center md:justify-start">
                            <div className="w-2 h-8 md:h-10 bg-yellow-500 rounded-full"></div>
                            <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23]">De onde vêm os valores da conta</h2>
                        </div>
                        <div className="flex-grow overflow-y-auto no-scrollbar flex flex-col justify-center gap-4 md:gap-6">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5">
                                <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 border-l-4 border-green-700">
                                    <h3 className="font-baloo font-bold text-[#4a5d23] mb-1.5 text-sm md:text-lg">Energia Ativa Fornecida</h3>
                                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">Consumo total registrado no seu medidor, cobrado ao preço cheio da tarifa da Equatorial.</p>
                                </div>
                                <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 border-l-4 border-green-700">
                                    <h3 className="font-baloo font-bold text-[#4a5d23] mb-1.5 text-sm md:text-lg">Energia Injetada (crédito Vigor)</h3>
                                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">Parte do consumo compensada pelos créditos da Vigor, com desconto aplicado.</p>
                                </div>
                                <div className="bg-white rounded-2xl shadow-md p-4 md:p-6 border-l-4 border-yellow-500">
                                    <h3 className="font-baloo font-bold text-[#4a5d23] mb-1.5 text-sm md:text-lg">Custo de disponibilidade</h3>
                                    <p className="text-gray-600 text-sm md:text-base leading-relaxed">Consumo mínimo do padrão de ligação — não entra na energia injetada.</p>
                                </div>
                            </div>

                            <div className="bg-[#4a5d23]/5 border border-[#4a5d23]/15 rounded-2xl p-5 md:p-8">
                                <h3 className="font-baloo font-bold text-[#4a5d23] mb-4 md:mb-5 flex items-center gap-2 text-base md:text-xl">
                                    <Calculator className="w-5 h-5 md:w-6 md:h-6" /> Como a energia compensada é calculada
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5">
                                    <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm">
                                        <p className="text-xs md:text-sm font-bold text-gray-400 uppercase tracking-wide mb-1.5">Energia compensada</p>
                                        <p className="text-sm md:text-base text-gray-800 font-medium">Consumo injetado × (Tarifa Equatorial − % desconto negociado)</p>
                                    </div>
                                    <div className="bg-white rounded-xl p-4 md:p-5 shadow-sm">
                                        <p className="text-xs md:text-sm font-bold text-gray-400 uppercase tracking-wide mb-1.5">Bandeira tarifária (quando aplicável)</p>
                                        <p className="text-sm md:text-base text-gray-800 font-medium">Consumo injetado × 50% do valor da tarifa da bandeira</p>
                                    </div>
                                </div>
                                <div className="mt-4 md:mt-5">
                                    <p className="text-sm md:text-base text-gray-600 mb-2.5">
                                        <strong className="text-gray-800">Importante:</strong> o consumo do custo de disponibilidade (tarifa mínima do relógio) não é considerado energia injetada:
                                    </p>
                                    <div className="flex flex-wrap gap-2 md:gap-3">
                                        <span className="bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs md:text-sm font-semibold text-gray-700">Monofásico: 30 kWh</span>
                                        <span className="bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs md:text-sm font-semibold text-gray-700">Bifásico: 50 kWh</span>
                                        <span className="bg-white border border-gray-200 rounded-full px-4 py-1.5 text-xs md:text-sm font-semibold text-gray-700">Trifásico: 100 kWh</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SLIDE 7 (APLICAÇÃO DO DESCONTO - EXEMPLO NUMÉRICO) */}
                <div className={`slide-transition ${currentSlide === 6 ? 'active' : ''} flex flex-col items-center justify-center bg-gray-50 text-gray-800 p-6 pb-16 md:p-12`}>
                    <div className="max-w-2xl w-full flex flex-col items-center text-center">
                        <div className="mb-4 md:mb-5 text-green-700 bg-green-50 p-3 md:p-4 rounded-full">
                            <Percent className="w-8 h-8 md:w-10 md:h-10" />
                        </div>
                        <h2 className="font-baloo text-2xl md:text-4xl font-extrabold text-[#4a5d23] mb-3 md:mb-4">Como o desconto é aplicado</h2>
                        <p className="text-sm md:text-lg text-gray-600 leading-relaxed max-w-xl mb-5 md:mb-6">
                            O desconto é aplicado sobre a tarifa da Equatorial e vira o valor do boleto Vigor Energy, referente à energia injetada.
                        </p>
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 md:p-8 w-full text-left">
                            <p className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 md:mb-4">Exemplo de cálculo</p>
                            <div className="space-y-2 md:space-y-3 text-[12px] md:text-base text-gray-700">
                                <div className="flex justify-between">
                                    <span>Consumo da unidade</span>
                                    <span className="font-semibold">1.030 kWh</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Tarifa Equatorial</span>
                                    <span className="font-semibold">R$ 1,16/kWh</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Desconto negociado Vigor Energy</span>
                                    <span className="font-semibold text-green-600">20%</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Custo de disponibilidade (monofásico)</span>
                                    <span className="font-semibold">30 kWh</span>
                                </div>
                                <div className="flex justify-between border-t border-gray-100 pt-2 md:pt-3">
                                    <span>Energia considerada para compensação</span>
                                    <span className="font-bold text-[#4a5d23]">1.000 kWh</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>Tarifa com desconto (R$ 1,16 × 80%)</span>
                                    <span className="font-bold text-[#4a5d23]">≈ R$ 0,92/kWh</span>
                                </div>
                                <div className="flex justify-between bg-green-50 rounded-lg px-3 py-2 mt-1 md:mt-2">
                                    <span className="font-bold text-green-700">Valor do boleto Vigor Energy</span>
                                    <span className="font-bold text-green-700">R$ 920,00</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SLIDE 8 (O MODELO E O BENEFÍCIO ECONÔMICO) */}
                <div className={`slide-transition ${currentSlide === 7 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-6 pb-16 md:p-12`}>
                    <div className="w-full max-w-5xl flex flex-col h-full justify-center">
                        <div className="flex items-center gap-4 mb-4 md:mb-8 self-center md:self-start w-full justify-center md:justify-start">
                            <div className="w-2 h-8 md:h-10 bg-green-600 rounded-full"></div>
                            <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23]">O modelo e o benefício</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 flex-grow overflow-y-auto no-scrollbar">
                            {[
                                { icon: ShieldCheck, title: "Não é compra de energia", desc: "Você adere a uma associação sem fins lucrativos de geração compartilhada (SCEE). A Vigor Energy não é concessionária nem comercializadora de energia elétrica.", tag: "SCEE — Lei 14.300/2022", color: "green" },
                                { icon: Percent, title: "Benefício econômico estimado", desc: "O desconto incide sobre a parcela compensável da fatura (Tarifa de Energia e parcela da TUSD). É estimado e pode variar — não é garantia contratual de valor fixo.", tag: "Base: TE + parcela da TUSD", color: "yellow" },
                                { icon: Clock, title: "Prazo para o 1º desconto", desc: "Após aprovação cadastral, a compensação pode levar até 90 dias, prorrogáveis por mais 60 dias em situações específicas (ex.: exigência da distribuidora).", tag: "Até 90 dias (+60 dias)", color: "green" },
                                { icon: RefreshCw, title: "Reajuste", desc: "A Contribuição Mensal é reajustada com base no mesmo índice de reajuste tarifário aplicado pela Equatorial.", tag: "Índice: reajuste Equatorial", color: "yellow" },
                            ].map((item, i) => {
                                const isGreen = item.color === "green";
                                return (
                                    <div key={i} className={`bg-white rounded-2xl shadow-md p-5 md:p-7 border-t-4 ${isGreen ? 'border-green-700' : 'border-yellow-500'} flex flex-col h-full`}>
                                        <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                                            <div className={`p-2.5 md:p-3 rounded-full flex-shrink-0 ${isGreen ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-600'}`}>
                                                <item.icon className="w-6 h-6 md:w-7 md:h-7" />
                                            </div>
                                            <h3 className="font-baloo font-bold text-[#4a5d23] text-base md:text-xl leading-tight">{item.title}</h3>
                                        </div>
                                        <p className="text-sm md:text-base text-gray-600 leading-relaxed flex-grow">{item.desc}</p>
                                        <div className="mt-4 md:mt-5 pt-3 md:pt-4 border-t border-gray-100">
                                            <span className={`inline-block text-xs md:text-sm font-bold px-3 py-1.5 rounded-full ${isGreen ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{item.tag}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* SLIDE 9 (PAGAMENTO, FIDELIDADE E SAÍDA) */}
                <div className={`slide-transition ${currentSlide === 8 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-6 pb-16 md:p-12`}>
                    <div className="w-full max-w-5xl flex flex-col h-full justify-center">
                        <div className="flex items-center gap-4 mb-4 md:mb-6 self-center md:self-start w-full justify-center md:justify-start">
                            <div className="w-2 h-8 md:h-10 bg-yellow-500 rounded-full"></div>
                            <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23]">Pagamento, fidelidade e saída</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 flex-grow overflow-y-auto no-scrollbar">
                            {[
                                { icon: ReceiptText, title: "Contribuição Mensal", desc: "Cobrada por boleto ou PIX, com vencimento até 5 dias antes do vencimento da Equatorial. A fatura da distribuidora continua sendo sua responsabilidade.", tag: "Vencimento: até 5 dias antes", color: "yellow" },
                                { icon: FileSignature, title: "Carência e Fidelidade", desc: "Quando previstas no seu Plano de Benefícios, cancelar antes do fim da Fidelidade sem justa causa pode gerar multa de até 30% do valor médio mensal × meses restantes.", tag: "Multa: até 30% × meses restantes", color: "green" },
                                { icon: XCircle, title: "Saída da associação", desc: "Aviso prévio mínimo de 90 dias. Sem multa durante a carência, em caso de justa causa da Vigor Energy ou impasse em reequilíbrio de condições.", tag: "Aviso prévio: 90 dias", color: "yellow" },
                            ].map((item, i) => {
                                const isGreen = item.color === "green";
                                return (
                                    <div key={i} className={`bg-white rounded-2xl shadow-md p-4 md:p-5 border-t-4 ${isGreen ? 'border-green-700' : 'border-yellow-500'} flex flex-col h-full`}>
                                        <div className="flex items-center gap-2.5 md:gap-3 mb-2 md:mb-2.5">
                                            <div className={`p-2 md:p-2.5 rounded-full flex-shrink-0 ${isGreen ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-600'}`}>
                                                <item.icon className="w-5 h-5 md:w-6 md:h-6" />
                                            </div>
                                            <h3 className="font-baloo font-bold text-[#4a5d23] text-sm md:text-base leading-tight">{item.title}</h3>
                                        </div>
                                        <p className="text-[13px] md:text-sm text-gray-600 leading-relaxed flex-grow">{item.desc}</p>
                                        <div className="mt-2.5 md:mt-3 pt-2 md:pt-2.5 border-t border-gray-100">
                                            <span className={`inline-block text-[11px] md:text-xs font-bold px-2.5 py-1 rounded-full ${isGreen ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>{item.tag}</span>
                                        </div>
                                    </div>
                                );
                            })}

                            <div className="md:col-span-3 bg-white rounded-2xl shadow-md p-4 md:p-6 border-t-4 border-red-400">
                                <div className="flex items-center gap-3 md:gap-4 mb-2.5 md:mb-4">
                                    <div className="p-2 md:p-2.5 rounded-full flex-shrink-0 bg-red-50 text-red-600">
                                        <Clock className="w-5 h-5 md:w-6 md:h-6" />
                                    </div>
                                    <h3 className="font-baloo font-bold text-[#4a5d23] text-sm md:text-base leading-tight">Atraso e inadimplência</h3>
                                </div>
                                <p className="text-[13px] md:text-sm text-gray-600 leading-relaxed mb-3 md:mb-4">
                                    Sobre valores em atraso incidem multa de 2% + juros de 1% ao mês, além da perda total ou parcial do Abatimento por Pontualidade da Contribuição Mensal.
                                </p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                                    {[
                                        { day: "Dia 1", desc: "Aviso do atraso e dos encargos." },
                                        { day: "Dia 10", desc: "Alerta de redução ou suspensão do benefício." },
                                        { day: "Dia 30", desc: "Perda do abatimento de pontualidade; débito pode ir a protesto." },
                                        { day: "Dia 60", desc: "Sem regularização, o contrato pode ser rescindido." },
                                    ].map((step, i) => (
                                        <div key={i} className="bg-red-50/60 rounded-xl p-2.5 md:p-3">
                                            <p className="font-baloo font-bold text-red-600 text-[13px] md:text-sm mb-0.5">{step.day}</p>
                                            <p className="text-[11px] md:text-xs text-gray-600 leading-snug">{step.desc}</p>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-[11px] md:text-xs text-gray-500 mt-2.5 md:mt-3">Após o protesto, o débito também pode ser negativado (SPC/Serasa).</p>
                            </div>

                            <div className="md:col-span-3 bg-[#4a5d23]/5 border border-[#4a5d23]/15 rounded-2xl p-4 md:p-5 flex items-center gap-3 md:gap-4">
                                <div className="bg-white text-[#4a5d23] p-2 md:p-2.5 rounded-full flex-shrink-0 shadow-sm">
                                    <RefreshCw className="w-5 h-5 md:w-6 md:h-6" />
                                </div>
                                <div>
                                    <h3 className="font-baloo font-bold text-[#4a5d23] mb-1 text-sm md:text-base">Créditos remanescentes</h3>
                                    <p className="text-[13px] md:text-sm text-gray-600 leading-relaxed">Créditos já gerados pelas usinas continuam sendo compensados e cobrados mesmo após a saída, por pelo menos 3 meses (podendo se estender conforme o calendário da distribuidora), até serem totalmente utilizados.</p>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 md:mt-4 justify-center">
                            <ScrollText className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-400" />
                            <p className="text-[10px] md:text-xs text-gray-500 text-center">Resumo simplificado — o Termo de Adesão e o Plano de Benefícios vigentes prevalecem em caso de dúvida.</p>
                        </div>
                    </div>
                </div>

                {/* SLIDE 10 (PERGUNTAS FREQUENTES) */}
                <div className={`slide-transition ${currentSlide === 9 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-6 pb-16 md:p-12 relative overflow-hidden`}>
                    <div className="w-full max-w-4xl flex flex-col h-full justify-start md:justify-center relative z-10 overflow-y-auto no-scrollbar pt-4 md:pt-0">
                        <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23] mb-4 md:mb-8 text-center flex-shrink-0 flex items-center justify-center gap-3">
                            <HelpCircle className="w-7 h-7 md:w-10 md:h-10 text-yellow-500" />
                            Perguntas Frequentes
                        </h2>

                        <div className="space-y-3 md:space-y-4 pb-12">
                            {faqItems.map((item, i) => (
                                <div
                                    key={i}
                                    className={`bg-white/70 rounded-2xl shadow-sm border border-gray-100 transition-all duration-300 overflow-hidden cursor-pointer ${activeFaq === i ? 'bg-white shadow-md' : 'hover:bg-white/90'}`}
                                    onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                                >
                                    <div className="p-4 md:p-5 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`text-[#ef9b1e] transition-transform duration-300 ${activeFaq === i ? 'rotate-90' : ''}`}>
                                                <Play className="w-4 h-4 md:w-5 md:h-5 fill-current" />
                                            </div>
                                            <h3 className={`md:text-lg font-bold transition-colors ${activeFaq === i ? 'text-green-700' : 'text-gray-800'}`}>
                                                {item.q}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className={`transition-all duration-500 ease-in-out ${activeFaq === i ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className="px-4 md:px-5 pb-5 pl-11 md:pl-12">
                                            <p className="text-gray-600 md:text-base leading-relaxed font-medium">
                                                {item.a}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* SLIDE 11 (RESUMO) */}
                <div className={`slide-transition ${currentSlide === 10 ? 'active' : ''} flex flex-col items-center justify-center bg-gradient-to-br from-white via-[#fcfaf8] to-[#fceee0] p-8 md:p-16 relative overflow-hidden`}>
                    <div className="absolute -top-20 -right-20 w-64 h-64 md:w-80 md:h-80 bg-green-100/50 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute -bottom-24 -left-24 w-72 h-72 md:w-96 md:h-96 bg-yellow-100/50 rounded-full blur-3xl pointer-events-none"></div>

                    <div className="max-w-4xl w-full flex flex-col items-center text-center relative z-10">
                        <div className="inline-flex items-center gap-2 bg-white/70 border border-green-200 rounded-full px-4 md:px-5 py-1.5 md:py-2 mb-5 md:mb-6 shadow-sm">
                            <Sparkles className="w-4 h-4 md:w-5 md:h-5 text-yellow-500" />
                            <span className="text-xs md:text-sm font-bold tracking-widest uppercase text-green-700">Em resumo</span>
                        </div>
                        <h2 className="font-baloo text-3xl md:text-5xl font-extrabold text-[#4a5d23] mb-3 md:mb-4 leading-tight">
                            Energia mais barata, <br className="hidden md:block" /><span className="text-green-600">sem complicação</span>
                        </h2>
                        <p className="text-sm md:text-lg text-gray-600 max-w-2xl mb-8 md:mb-12">
                            Tudo o que você precisa saber sobre a Vigor Energy em três pontos.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 w-full">
                            {[
                                { icon: Wallet, title: "Sem investimento", desc: "Sem equipamentos, sem taxa de adesão.", color: "green" },
                                { icon: Percent, title: "Até 28% de desconto", desc: "Aplicado direto na fatura da distribuidora.", color: "yellow" },
                                { icon: Smartphone, title: "100% digital", desc: "Do cadastro à gestão do plano, tudo online.", color: "green" },
                            ].map((item, i) => {
                                const isGreen = item.color === "green";
                                return (
                                    <div key={i} className={`bg-white rounded-2xl shadow-lg p-6 md:p-8 border-t-4 ${isGreen ? 'border-green-700' : 'border-yellow-500'} flex flex-col items-center text-center gap-2 md:gap-3 transition-transform hover:-translate-y-1 duration-300`}>
                                        <div className={`p-3 md:p-4 rounded-full ${isGreen ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-600'}`}>
                                            <item.icon className="w-7 h-7 md:w-8 md:h-8" />
                                        </div>
                                        <p className="font-baloo font-bold text-[#4a5d23] text-base md:text-lg">{item.title}</p>
                                        <p className="text-gray-600 text-sm md:text-base leading-relaxed">{item.desc}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* SLIDE 12 (DÚVIDAS / CONTATO) */}
                <div className={`slide-transition ${currentSlide === 11 ? 'active' : ''} flex bg-gray-100 overflow-hidden relative`}>
                    <div className="absolute inset-0 w-full h-full flex justify-end">
                        <div className="w-full md:w-[65%] h-full relative">
                            <img
                                src="https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                                alt="Consultor Vigor Energy"
                                className="w-full h-full object-cover object-top"
                            />
                            <img
                                src="https://vigorenergy.com.br/wp-content/uploads/2024/07/LOGO-01.webp"
                                alt="Vigor Energy Watermark"
                                className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2 md:-translate-x-1/3 w-[300px] md:w-[600px] opacity-[0.08] object-contain grayscale pointer-events-none hidden md:block"
                            />
                            <div className="absolute inset-0 bg-white/40 md:hidden"></div>
                        </div>
                    </div>

                    <div className="relative z-10 w-full md:w-[48%] h-full bg-white/95 md:bg-white md:rounded-r-[3rem] shadow-[15px_0_30px_rgba(0,0,0,0.15)] flex flex-col justify-center p-8 pb-20 md:p-20 overflow-y-auto no-scrollbar">
                        <div className="mb-4 md:mb-6 text-green-700">
                            <MessageCircle className="w-9 h-9 md:w-10 md:h-10" />
                        </div>
                        <h2 className="font-baloo text-4xl md:text-5xl font-extrabold text-[#4a5d23] mb-4 md:mb-6 leading-tight">
                            Ficou com<br />alguma dúvida?
                        </h2>

                        <p className="text-gray-700 md:text-lg mb-8 md:mb-10 leading-relaxed font-medium">
                            Nosso time de consultores está pronto para te explicar qualquer ponto deste manual em detalhes.
                        </p>

                        <a href="https://wa.me/5562991183449?text=Ol%C3%A1%2C%20tenho%20uma%20d%C3%BAvida%20sobre%20o%20Manual%20do%20Consumidor%20da%20Vigor%20Energy." target="_blank" className="inline-block bg-[#ef9b1e] hover:bg-[#d88a18] text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded shadow-lg transition-transform hover:-translate-y-1 w-max md:text-lg tracking-wide uppercase mb-6 active:scale-95">
                            Falar com um consultor
                        </a>
                    </div>
                </div>

            </div>

            {/* CONTROLES (SETAS) */}
            <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 bg-gray-800/10 hover:bg-gray-800/20 backdrop-blur-md p-2 md:p-4 rounded-full shadow-md text-white md:text-gray-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed border border-white/20 md:border-gray-800/20 z-50"
            >
                <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
            </button>

            <button
                onClick={nextSlide}
                disabled={currentSlide === slidesCount - 1}
                className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 bg-gray-800/10 hover:bg-gray-800/20 backdrop-blur-md p-2 md:p-4 rounded-full shadow-md text-white md:text-gray-800 transition-all disabled:opacity-30 disabled:cursor-not-allowed border border-white/20 md:border-gray-800/20 z-50"
            >
                <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
            </button>

            {/* Indicador de progresso (Bolinhas) */}
            <div className="absolute bottom-6 flex gap-3 z-20">
                {Array.from({ length: slidesCount }).map((_, i) => (
                    <div
                        key={i}
                        onClick={() => goToSlide(i)}
                        className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${i === currentSlide ? 'bg-white md:bg-gray-800 scale-125' : 'bg-gray-500 md:bg-gray-300 hover:bg-gray-400'
                            }`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ManualConsumidorView;
